import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { searchMedia } from "@/lib/mediaQuery";
import { mediaList } from "@/mocks/media";
import { useSeo } from "@/hooks/useSeo";
import MediaBrowser from "@/components/feature/MediaBrowser";
import MediaCard from "@/components/feature/MediaCard";

const hotKeywords = ["悬疑", "科幻", "国产剧", "家庭", "犯罪", "经典"];

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const keyword = params.get("q")?.trim() ?? "";
  const [input, setInput] = useState(keyword);

  const results = useMemo(() => searchMedia(keyword), [keyword]);
  const recommend = useMemo(() => [...mediaList].sort((a, b) => b.rating - a.rating).slice(0, 4), []);

  useSeo({
    title: keyword ? `“${keyword}”的搜索结果 - 缘之空影视资讯平台` : "搜索影视 - 缘之空影视资讯平台",
    description: keyword
      ? `缘之空为你找到与“${keyword}”相关的影视作品，支持按地区、年份、评分与剧情标签进一步筛选。`
      : "在缘之空搜索电影、电视剧名称或剧情关键词，快速定位你感兴趣的影视作品。",
    keywords: `缘之空,影视搜索,${keyword || "电影搜索"},剧情查询`,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="text-foreground-800">搜索</span>
      </nav>

      <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
        {keyword ? (
          <>
            搜索“<span className="text-primary-600">{keyword}</span>”
          </>
        ) : (
          "搜索影视"
        )}
      </h1>

      <form onSubmit={submit} className="flex items-center gap-2 max-w-xl mb-8">
        <div className="flex-1 flex items-center gap-2 bg-background-50 border border-background-300 rounded-md px-4 py-2.5 focus-within:border-primary-400">
          <i className="ri-search-line text-foreground-400"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影视名称、导演或剧情关键词"
            className="flex-1 bg-transparent text-sm outline-none text-foreground-800 placeholder:text-foreground-400 min-w-0"
          />
        </div>
        <button
          type="submit"
          className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          搜索
        </button>
      </form>

      {keyword && (
        <p className="text-sm text-foreground-500 mb-5">
          共找到 <span className="text-primary-600 font-medium">{results.length}</span> 条与“{keyword}”相关的结果
        </p>
      )}

      {keyword && results.length > 0 && <MediaBrowser items={results} />}

      {keyword && results.length === 0 && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-8 md:p-10 text-center mb-10">
          <span className="w-16 h-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-4 mx-auto">
            <i className="ri-search-2-line text-3xl"></i>
          </span>
          <h2 className="font-heading text-lg font-bold text-foreground-900 mb-2">暂无相关内容</h2>
          <p className="text-sm text-foreground-500 max-w-md mx-auto mb-6">
            没有找到与“{keyword}”匹配的影视，你可以返回首页、尝试其他关键词，或看看下面的热门推荐。
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-7">
            {hotKeywords.map((k) => (
              <Link
                key={k}
                to={`/search?q=${encodeURIComponent(k)}`}
                className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-sm hover:bg-secondary-200 transition-colors whitespace-nowrap"
              >
                {k}
              </Link>
            ))}
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      )}

      {!keyword && (
        <div className="mb-10">
          <h2 className="font-heading text-base font-bold text-foreground-900 mb-3">大家都在搜</h2>
          <div className="flex flex-wrap gap-2">
            {hotKeywords.map((k) => (
              <Link
                key={k}
                to={`/search?q=${encodeURIComponent(k)}`}
                className="px-3.5 py-2 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                {k}
              </Link>
            ))}
          </div>
        </div>
      )}

      {(!keyword || results.length === 0) && (
        <section>
          <h2 className="font-heading text-base font-bold text-foreground-900 mb-5">高口碑影视推荐</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {recommend.map((item, i) => (
              <MediaCard key={item.id} item={item} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}